import React, {useEffect, useMemo, useState} from 'react';
import styles from '../../../../components/StackedBarChart/StackedBarChart.module.css';
import useApi from '../../../../hooks/useApi.hook';
import {
	getSizNeedByMaterialTypeUrl,
	SIZ_NEED_STATUS_COLORS,
	SIZ_NEED_STATUS_LABELS
} from '../../../../helpers/constants';
import {
	BarChart,
	Bar,
	Cell,
	XAxis,
	YAxis,
	CartesianGrid,
	Legend,
	ResponsiveContainer
} from 'recharts';
import ErrorMessage from '../../../../components/ErrorMessage/ErrorMessage';
import Spinner from '../../../../components/Spinner/Spinner';
import Button from '../../../../components/Button/Button';
import {
	LEGEND_STYLE,
	AXIS_TICK_STYLE,
	Y_AXIS_WIDTH,
	BAR_SIZE,
	CHART_MARGIN,
	calcChartHeight,
	createStackedBarShape,
	FullBarOutlineShape,
	CustomYAxisTick,
	toPercent,
	fillOpacityFor
} from '../../../../components/StackedBarChart/StackedBarChartUtils';

const STACK_SEGMENT_KEYS = ['covered', 'need'];

export function SIZChart({onGoToTable}) {
	const api = useApi();
	const [data, setData] = useState([]);
	const [selectedTypeId, setSelectedTypeId] = useState(undefined);
	const [hoveredTypeId, setHoveredTypeId] = useState(undefined);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(undefined);
	const selectedType = useMemo(
		() => data.find(item => item.materialTypeId === selectedTypeId) || data[0] || null,
		[data, selectedTypeId]
	);

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			try {
				const response = await api.get(getSizNeedByMaterialTypeUrl);
				const chartData = (response.data || [])
					.filter(row => (row.norm ?? 0) > 0)
					.map(row => {
						const norm = row.norm ?? 0;
						const given = row.given ?? 0;
						const need = row.need ?? 0;
						return {
							materialTypeId: row.material_type_id,
							name: row.material_type_name,
							unit: row.unit,
							norm,
							given,
							need,
							covered: Math.max(norm - need, 0)
						};
					});
				setData(chartData);
				setSelectedTypeId(chartData[0]?.materialTypeId);
			} catch (e) {
				setError(e.response?.data?.detail || e.response?.data?.message || 'Ошибка загрузки данных');
			} finally {
				setLoading(false);
			}
		};
		fetchData();
	}, []);

	if (loading) return <Spinner />;
	if (error) return <ErrorMessage error={error} onClose={() => setError(undefined)} />;
	if (data.length === 0) return <div className={styles.message}>Нет данных для отображения</div>;
	if (!selectedType) return <div className={styles.message}>Нет данных для отображения</div>;

	const chartHeight = calcChartHeight(data.length);
	const activeIndex = data.findIndex(item => item.materialTypeId === selectedTypeId);
	const hoveredIndex = hoveredTypeId == null
		? undefined
		: data.findIndex(item => item.materialTypeId === hoveredTypeId);
	const hoverRow = (index) => {
		setHoveredTypeId(index == null ? undefined : data[index]?.materialTypeId);
	};
	const selectRow = (index) => {
		const materialTypeId = data[index]?.materialTypeId;
		if (materialTypeId) {
			setSelectedTypeId(materialTypeId);
		}
	};

	return (
		<div className={styles.wrapper}>
			<div className={styles.chartColumn}>
				<div className={styles.header}>
					<h3 className={styles.title}>Статистика потребности в СИЗ</h3>
					<span className={styles.subtitle}>Клик по строке открывает детали</span>
				</div>
				<div className={styles.chartCanvas} style={{height: `${chartHeight}px`}}>
					<ResponsiveContainer width="100%" height="100%" debounce={50}>
						<BarChart
							data={data}
							layout="vertical"
							margin={CHART_MARGIN}
							barSize={BAR_SIZE}
							barCategoryGap={8}
							barGap={-BAR_SIZE}
							onMouseMove={(state, event) => {
								if (event?.target?.closest?.('.chart-row-tick')) {
									return;
								}
								const hovered = state?.activePayload?.[0]?.payload;
								setHoveredTypeId(hovered?.materialTypeId);
							}}
							onMouseLeave={() => setHoveredTypeId(undefined)}
							onClick={(state) => {
								const clicked = state?.activePayload?.[0]?.payload;
								if (clicked?.materialTypeId) {
									setSelectedTypeId(clicked.materialTypeId);
								}
							}}
						>
							<CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
							<XAxis
								type="number"
								allowDecimals={false}
								tick={AXIS_TICK_STYLE}
							/>
							<YAxis
								type="category"
								dataKey="name"
								tick={(tickProps) => (
									<CustomYAxisTick
										{...tickProps}
										activeIndex={activeIndex}
										hoveredIndex={hoveredIndex}
										onHoverIndex={hoverRow}
										onSelectIndex={selectRow}
									/>
								)}
								width={Y_AXIS_WIDTH}
								interval={0}
							/>
							<Legend
								formatter={(value) => SIZ_NEED_STATUS_LABELS[value] || value}
								wrapperStyle={LEGEND_STYLE}
								verticalAlign="top"
							/>
							<Bar
								dataKey="covered"
								stackId="a"
								fill={SIZ_NEED_STATUS_COLORS.covered}
								name="covered"
								shape={createStackedBarShape('covered', STACK_SEGMENT_KEYS)}
							>
								{data.map((entry) => (
									<Cell
										key={`covered-${entry.materialTypeId}`}
										fill={SIZ_NEED_STATUS_COLORS.covered}
										fillOpacity={fillOpacityFor(entry.materialTypeId, selectedTypeId, hoveredTypeId)}
									/>
								))}
							</Bar>
							<Bar
								dataKey="need"
								stackId="a"
								fill={SIZ_NEED_STATUS_COLORS.need}
								name="need"
								shape={createStackedBarShape('need', STACK_SEGMENT_KEYS)}
							>
								{data.map((entry) => (
									<Cell
										key={`need-${entry.materialTypeId}`}
										fill={SIZ_NEED_STATUS_COLORS.need}
										fillOpacity={fillOpacityFor(entry.materialTypeId, selectedTypeId, hoveredTypeId)}
									/>
								))}
							</Bar>
							<Bar
								dataKey="norm"
								fill="transparent"
								stroke={SIZ_NEED_STATUS_COLORS.norm}
								strokeWidth={1.2}
								name="norm"
								legendType="plain"
								shape={FullBarOutlineShape}
								isAnimationActive={false}
							>
								{data.map((entry) => {
									const isSelected = entry.materialTypeId === selectedTypeId;
									const hasHover = hoveredTypeId !== undefined;
									const isHovered = entry.materialTypeId === hoveredTypeId;
									const strokeOpacity = hasHover ? (isHovered ? 0.8 : 0.35) : (isSelected ? 0.8 : 0.55);
									return (
										<Cell
											key={`norm-${entry.materialTypeId}`}
											fill="transparent"
											stroke={SIZ_NEED_STATUS_COLORS.norm}
											strokeOpacity={strokeOpacity}
											strokeWidth={isSelected ? 1.4 : 1.2}
										/>
									);
								})}
							</Bar>
						</BarChart>
					</ResponsiveContainer>
				</div>
			</div>
			<aside className={styles.detailsCard}>
				<div className={styles.detailsHeader}>
					<div className={styles.detailsTitle}>{selectedType.name}</div>
					{onGoToTable && (
						<Button
							className={styles.toTableButton}
							onClick={() => onGoToTable(selectedType.materialTypeId)}
						>
							В таблицу
						</Button>
					)}
				</div>
				<div className={styles.metrics}>
					<div className={styles.metricRow}>
						<span className={styles.metricLabel}>Норма</span>
						<span className={styles.metricValueNoData}>
							{selectedType.norm}
							{selectedType.unit ? ` ${selectedType.unit}` : ''}
						</span>
					</div>
					<div className={styles.metricRow}>
						<span className={styles.metricLabel}>Выдано</span>
						<span className={styles.metricValueOk}>
							{selectedType.given} ({toPercent(selectedType.given, selectedType.norm)})
						</span>
					</div>
					<div className={styles.metricRow}>
						<span className={styles.metricLabel}>Необходимо</span>
						<span className={styles.metricValueOverdue}>
							{selectedType.need} ({toPercent(selectedType.need, selectedType.norm)})
						</span>
					</div>
				</div>
			</aside>
		</div>
	);
}
